import { t } from '@app/i18n';
import { Controller, useFieldArray, type Control } from 'react-hook-form';
import { Icon } from '@zarya/theme';
import {
  Field,
  FieldGrid,
  IconButton,
  SearchCombobox,
  TextInput,
  type ComboOption,
} from '@zarya/ui';
import {
  AUTHORIZATION,
  MAX_HEADERS,
  PROTOCOLS,
  callAddress,
  newHeader,
  secretMask,
  tokenHeaderOf,
  type ChatEntryForm as ChatEntryFormValues,
} from './ChatEntryModel';
import s from './Chat.module.css';

export interface ChatEntryFormProps {
  control: Control<ChatEntryFormValues>;
  formId: string;
  /** The approver sees the entry and does not change it (D5). */
  readOnly: boolean;
  /** The current values, for the preview and the conditional parts. */
  values: ChatEntryFormValues;
  onSubmit: () => void;
}

/**
 * The fields of the drawer (prototype v2 §3.2). The protocol values and `Authorization` are contract values, the
 * same in both languages; only their explanation is in the catalog.
 */
export function ChatEntryForm({ control, formId, readOnly, values, onSubmit }: ChatEntryFormProps) {
  const headers = useFieldArray({ control, name: 'headers' });
  const protocolOptions: ComboOption[] = PROTOCOLS.map((protocol) => ({
    value: protocol,
    label: t(`chat.entry.field.protocol.option.${protocol}`),
  }));

  return (
    <form
      id={formId}
      className={s.entryForm}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      noValidate
    >
      <FieldGrid>
        <Controller
          control={control}
          name="service"
          render={({ field, fieldState }) => (
            <Field
              label={t('chat.entry.field.service.label')}
              span={12}
              tip={t('chat.entry.field.service.help')}
              error={fieldState.error?.message}
            >
              {(props) => (
                <TextInput {...props} {...field} disabled={readOnly} spellCheck={false} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name="port"
          render={({ field, fieldState }) => (
            <Field
              label={t('chat.entry.field.port.label')}
              span={4}
              tip={t('chat.entry.field.port.help')}
              error={fieldState.error?.message}
            >
              {(props) => (
                <TextInput {...props} {...field} disabled={readOnly} inputMode="numeric" />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name="path"
          render={({ field, fieldState }) => (
            <Field
              label={t('chat.entry.field.path.label')}
              span={8}
              tip={t('chat.entry.field.path.help')}
              error={fieldState.error?.message}
            >
              {(props) => (
                <TextInput {...props} {...field} disabled={readOnly} spellCheck={false} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name="protocol"
          render={({ field }) => (
            <Field
              label={t('chat.entry.field.protocol.label')}
              span={12}
              tip={t('chat.entry.field.protocol.help')}
            >
              {({ id, className: _class, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('chat.entry.field.protocol.label')}
                  options={protocolOptions}
                  value={field.value}
                  disabled={readOnly}
                  onChange={(value) => value && field.onChange(value)}
                />
              )}
            </Field>
          )}
        />
      </FieldGrid>

      <Controller
        control={control}
        name="tokenHeaderMode"
        render={({ field }) => (
          <fieldset className={s.entryFieldset} disabled={readOnly}>
            <legend className={s.entryLegend}>{t('chat.entry.field.tokenHeader.label')}</legend>
            <p className={s.entryHelp}>{t('chat.entry.field.tokenHeader.help')}</p>
            <label className={s.entryOption}>
              <input
                type="radio"
                name={field.name}
                value="authorization"
                checked={field.value === 'authorization'}
                onChange={() => field.onChange('authorization')}
              />
              {t('chat.entry.field.tokenHeader.option.authorization')}
            </label>
            <label className={s.entryOption}>
              <input
                type="radio"
                name={field.name}
                value="other"
                checked={field.value === 'other'}
                onChange={() => field.onChange('other')}
              />
              {t('chat.entry.field.tokenHeader.option.custom')}
            </label>
            {field.value === 'other' ? (
              <Controller
                control={control}
                name="tokenHeaderName"
                render={({ field: name, fieldState }) => (
                  <Field
                    label={t('chat.entry.field.tokenHeader.name')}
                    span={12}
                    error={fieldState.error?.message}
                  >
                    {(props) => <TextInput {...props} {...name} spellCheck={false} />}
                  </Field>
                )}
              />
            ) : null}
          </fieldset>
        )}
      />

      <fieldset className={s.entryFieldset} disabled={readOnly} data-testid="chat-entry-headers">
        <legend className={s.entryLegend}>{t('chat.entry.field.headers.label')}</legend>
        <p className={s.entryHelp}>{t('chat.entry.field.headers.help')}</p>
        {headers.fields.length === 0 ? (
          <p className={s.entryEmpty} data-testid="chat-entry-headers-empty">
            {t('chat.entry.field.headers.empty')}
          </p>
        ) : null}
        {headers.fields.map((row, i) => (
          <HeaderRow
            key={row.id}
            control={control}
            index={i}
            readOnly={readOnly}
            header={values.headers?.[i] ?? row}
            onRemove={() => headers.remove(i)}
          />
        ))}
        {!readOnly && headers.fields.length < MAX_HEADERS ? (
          <button type="button" className={s.addPair} onClick={() => headers.append(newHeader())}>
            <Icon name="plus" size="sm" aria-hidden="true" />
            {t('chat.entry.field.headers.add')}
          </button>
        ) : null}
      </fieldset>

      <Controller
        control={control}
        name="opensChat"
        render={({ field }) => (
          <label className={s.entryOption} data-testid="chat-entry-opens-chat">
            <input
              type="checkbox"
              checked={field.value}
              disabled={readOnly}
              onChange={(event) => field.onChange(event.target.checked)}
            />
            {t('chat.entry.field.default.label')}
            <span className={s.entryHelp}>{t('chat.entry.field.default.help')}</span>
          </label>
        )}
      />

      <section
        className={s.entryPreview}
        aria-label={t('chat.entry.preview.label')}
        data-testid="chat-entry-preview"
      >
        <p className={s.entryLegend}>{t('chat.entry.preview.label')}</p>
        <code className={s.mono} data-testid="chat-entry-address">
          {callAddress(values)}
        </code>
        <p className={s.entryHelp}>
          {t('chat.entry.preview.token', { header: tokenHeaderOf(values) || AUTHORIZATION })}
        </p>
      </section>
    </form>
  );
}

function HeaderRow({
  control,
  index,
  readOnly,
  header,
  onRemove,
}: {
  control: Control<ChatEntryFormValues>;
  index: number;
  readOnly: boolean;
  header: ChatEntryFormValues['headers'][number];
  onRemove: () => void;
}) {
  const keeping = header.sensitive && header.storedHint !== null && !header.replacing;
  return (
    <div
      className={s.entryHeader}
      data-testid="chat-entry-header"
      data-sensitive={header.sensitive ? '' : undefined}
    >
      <Controller
        control={control}
        name={`headers.${index}.name`}
        render={({ field, fieldState }) => (
          <Field
            label={t('chat.entry.field.headers.namePlaceholder')}
            span={4}
            error={fieldState.error?.message}
          >
            {(props) => <TextInput {...props} {...field} disabled={readOnly} spellCheck={false} />}
          </Field>
        )}
      />
      {keeping ? (
        <div className={s.entrySecretKept} data-testid="chat-entry-secret-mask">
          <Icon name="lock" size="sm" aria-hidden="true" />
          <span className={s.mono}>{secretMask(header.storedHint)}</span>
          {!readOnly ? (
            <Controller
              control={control}
              name={`headers.${index}.replacing`}
              render={({ field }) => (
                <button type="button" className={s.entryLink} onClick={() => field.onChange(true)}>
                  {t('chat.entry.field.headers.replace')}
                </button>
              )}
            />
          ) : null}
        </div>
      ) : (
        <Controller
          control={control}
          name={`headers.${index}.value`}
          render={({ field, fieldState }) => (
            <Field
              label={t('chat.entry.field.headers.valuePlaceholder')}
              span={6}
              error={fieldState.error?.message}
              help={header.replacing ? t('chat.entry.field.headers.keepHint') : undefined}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  disabled={readOnly}
                  type={header.sensitive ? 'password' : 'text'}
                  autoComplete={header.sensitive ? 'new-password' : 'off'}
                />
              )}
            </Field>
          )}
        />
      )}
      {header.replacing && !readOnly ? (
        <Controller
          control={control}
          name={`headers.${index}.replacing`}
          render={({ field }) => (
            <button type="button" className={s.entryLink} onClick={() => field.onChange(false)}>
              {t('chat.entry.field.headers.keepCurrent')}
            </button>
          )}
        />
      ) : null}
      <Controller
        control={control}
        name={`headers.${index}.sensitive`}
        render={({ field }) => (
          <label className={s.entryOption} title={t('chat.entry.field.headers.secretTip')}>
            <input
              type="checkbox"
              checked={field.value}
              disabled={readOnly || keeping}
              onChange={(event) => field.onChange(event.target.checked)}
            />
            {t('chat.entry.field.headers.secret')}
          </label>
        )}
      />
      {!readOnly ? (
        <IconButton
          icon="close"
          size="sm"
          label={t('chat.entry.field.headers.remove', { n: String(index + 1) })}
          onClick={onRemove}
        />
      ) : null}
    </div>
  );
}
